// How the audit trail's "Object" and "Sample / lot" columns are rendered — shared by BOTH audit
// surfaces: the global Audit Trail screen (`screens/LimsScreen/AuditViewer`) and the analysis-task
// audit section (`components/Lims/ResultEntryPanel/AuditSection`).
//
// It lives in one module deliberately (SPEC-049 R12). `objectTypeLabel` was previously duplicated
// byte-for-byte in both files, and that duplication is exactly how RR8 #4063 and #4068 came to be
// filed as two separate issues: one root cause, two surfaces, no shared code to fix once. #4138 /
// #4156 are the same shape again, which is why the SAMPLE cell is resolved here too and not
// inlined in the two tables.

// A canonical UUID. This is the shape the pre-fix backend wrote into `object_label`, the column
// reserved for the HUMAN label (backend SPEC-052 R11 closed the write path).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// What an empty audit cell looks like. One constant so the two surfaces cannot drift apart.
export const AUDIT_EMPTY = "—"

// The object types whose audited object IS a sample / lot: the backend writes the LOT NAME into
// `object_label` for every one of them (`sample` → `sample_observation.sample_name` / the QC
// unique sample id, `oos` / `investigation` / `capa` / `qaDecision` → `oos.sample_label`,
// `rerun` → `qc_rerun.sample_number`).
//
// That is #4156: the lot landed in whichever column the writing action happened to use — Objekt
// for these types, "Sample / lot" for a `result` entry — so the customer saw the lot jump between
// two columns depending on the action. The rule settled with the customer is that a lot ALWAYS
// reads in the "Sample / lot" column, so these types are re-routed at READ time here.
//
// `result` is deliberately NOT in this set: its `object_label` is the ANALYSIS name and the
// backend already resolves its lot into `sampleLabel`, so it is the shape everything else is being
// made to match. Neither is `sst` (keyed on the analysis RUN, covers every lot on the tray) nor
// `batch` (keyed on the lot STRING, but the audited object is the batch, not one sample).
export const SAMPLE_SCOPED_OBJECT_TYPES = [
  "sample",
  "oos",
  "investigation",
  "capa",
  "qaDecision",
  "rerun",
]

function isSampleScoped(entry) {
  return !!entry && SAMPLE_SCOPED_OBJECT_TYPES.includes(entry.objectType)
}

// A stored label is only usable if it is human — never a bare UUID (see UUID_RE).
function humanLabel(value) {
  return value && !UUID_RE.test(value) ? value : null
}

export function objectTypeLabel(typ, t) {
  if (!typ) return ""
  if (typ === "member") return t("member")
  if (typ === "product") return t("limsAuditTypeProduct")
  if (typ === "consumable") return t("limsAuditTypeConsumable")
  if (typ === "reagent") return t("limsAuditTypeReagent")
  if (typ === "oos") return t("limsAuditTypeOos")
  if (typ === "sample") return t("limsAuditTypeSample")
  if (typ === "result") return t("limsAuditTypeResult")
  if (typ === "forskrift") return t("limsAuditTypeForskrift")
  if (typ === "qaDecision") return t("limsAuditTypeQaDecision")
  if (typ === "batch") return t("limsAuditTypeBatch")
  if (typ === "rerun") return t("limsAuditTypeRerun")
  if (typ === "investigation") return t("limsAuditTypeInvestigation")
  if (typ === "capa") return t("limsAuditTypeCapa")
  if (typ === "sst") return t("limsAuditTypeSst")
  if (typ === "commentTemplate") return t("limsAuditTypeCommentTemplate")
  return typ
}

// The Object cell for one audit entry (SPEC-049 R12, #4156).
//
// Sample-scoped entry: the lot has moved to the "Sample / lot" cell, so Objekt shows the ANALYSIS
// the action came through when the entry names one, and an em dash when it does not. It does NOT
// fall back to the object type here — repeating "Sample" / "OOS" next to the lot the reader can
// already see in the neighbouring column adds nothing, and the type is what the screen's own type
// filter is for.
//
// Everything else: prefer the stored human label, but NEVER render a bare UUID. Rows written
// before the backend fix carry the analysis id in `object_label`, and the audit table is
// append-only — those rows are deliberately never rewritten, so the fallback is permanent, not
// transitional. A UUID tells an auditor nothing, whereas the object TYPE ("Result", "Sample") at
// least says what was acted on.
//
// DERIVED AT READ TIME, on purpose: `lims_audit` is append-only and immutable (GMP), so no row is
// ever migrated to match a new display rule — the rule has to make the EXISTING rows read
// correctly, which is exactly what routing on `objectType` does.
export function auditObjectLabel(entry, t) {
  if (isSampleScoped(entry)) {
    // `analysisLabel` is resolved server-side at read time and is absent until that lands (and
    // for any entry whose analysis can no longer be resolved) — degrade to an em dash, never to
    // the lot, which now belongs to the other column.
    return humanLabel(entry.analysisLabel) || AUDIT_EMPTY
  }
  const label = entry && entry.objectLabel
  if (label && !UUID_RE.test(label)) return label
  return objectTypeLabel(entry && entry.objectType, t)
}

// The "Sample / lot" cell for one audit entry (#4138 / #4156).
//
// `sampleLabel` is the backend's read-time resolution of the lot (it is not a stored column — see
// the route's `_attach_sample_labels`) and is authoritative whenever it is present. When it is
// not, a sample-scoped entry still carries the lot in its own `object_label`, which is what makes
// the historical rows read correctly with no migration.
//
// The `analysisLabel` guard matters for `investigation` entries: the backend writes
// `oos.sample_label or oos.analysis_label` into `object_label`, so on a case with no sample label
// the stored label is the ANALYSIS name — and copying that into the lot column would be worse
// than leaving the cell empty.
export function auditSampleLabel(entry) {
  const resolved = entry && entry.sampleLabel
  if (resolved) return resolved
  if (isSampleScoped(entry)) {
    const stored = humanLabel(entry.objectLabel)
    if (stored && stored !== entry.analysisLabel) return stored
  }
  return AUDIT_EMPTY
}
